import { useCallback, useEffect, useMemo, useState } from "react";
import { resolveUrl, resolveEffectivePrice, trackAddToCart, trackCartUpdated, trackRemovedFromCart } from "../webOrderApi";

function cartKeyFor(productId, variantId) {
  return `${productId}-${variantId || "base"}`;
}

const CART_STORAGE_KEY = "web_order_cart";

// Extracted verbatim from WebOrderPage.jsx so /order and /menu share ONE
// cart implementation — same localStorage key, same pricing/grouping rules,
// same analytics tracking — rather than risking two independently-maintained
// copies drifting apart. `products` and `sessionId`/`resolvingIdentity` come
// from the page using this (each page fetches its own products list /
// resolves its own identity via useIdentity — see those call sites), `ready`
// is that page's own `brand` (storefront branding) having loaded —
// everything else (cart state, mutation, toast, derived totals) lives here.
export default function useCart(products, sessionId, resolvingIdentity, ready) {
  const [cartItems, setCartItems] = useState(() => {
    try {
      const saved = localStorage.getItem(CART_STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [toastMessage, setToastMessage] = useState(null);

  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
    } catch {
      /* ignore — cart just won't survive a refresh */
    }
  }, [cartItems]);

  // Same "send the full cart on every change" rule as WebOrderPage — see its
  // own comment on why this backs the seller's Live Chat "Cart" indicator.
  // Gated on ready+resolvingIdentity, matching WebOrderPage's own original
  // gate exactly: firing before identity resolution settles would send this
  // snapshot with a not-yet-final sessionId, permanently losing its
  // attribution once the real one resolves a moment later.
  useEffect(() => {
    if (!ready || resolvingIdentity) return;
    trackCartUpdated(
      sessionId,
      cartItems
        .filter((item) => !item.zeroPriced)
        .map((item) => ({
          name: item.variantLabel ? `${item.name} (${item.variantLabel})` : item.name,
          quantity: item.quantity,
          price: item.unitPrice,
        })),
    );
  }, [cartItems, sessionId, resolvingIdentity, ready]);

  const showToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 2500);
  };

  const addToCart = useCallback((product, variant, qty = 1, options = {}) => {
    const cartKey = cartKeyFor(product.id, variant?.id);
    const useMainPrice = product.useMainProductPrice === true;
    const zeroPriced = options.zeroPriced === true;
    const originalPrice = zeroPriced
      ? 0
      : useMainPrice
        ? Number(product.price)
        : variant && Number(variant.price) > 0 ? Number(variant.price) : Number(product.price);
    const unitPrice = zeroPriced
      ? 0
      : useMainPrice
        ? resolveEffectivePrice(product.price, product.salePrice)
        : variant && Number(variant.price) > 0
          ? resolveEffectivePrice(variant.price, variant.salePrice)
          : resolveEffectivePrice(product.price, product.salePrice);
    setCartItems((prev) => {
      const idx = prev.findIndex((i) => i.cartKey === cartKey);
      if (idx > -1) {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], quantity: qty, zeroPriced };
        return updated;
      }
      return [
        ...prev,
        {
          cartKey,
          productId: product.id,
          variantId: variant?.id || null,
          name: product.name,
          variantLabel: variant?.label || null,
          image: resolveUrl(product.images?.[0]),
          unitPrice,
          originalPrice,
          quantity: qty,
          zeroPriced,
        },
      ];
    });
    showToast(`Cart updated — ${qty}x "${product.name}${variant ? ` (${variant.label})` : ""}"`);
    trackAddToCart(sessionId, {
      productId: product.id,
      productName: variant ? `${product.name} (${variant.label})` : product.name,
      productPrice: unitPrice,
      quantity: qty,
    });
  }, [sessionId]);

  // Fires one REMOVED_FROM_CART row per cart line actually leaving the
  // cart — a quantity edit that stays above zero never calls this. Takes
  // the cart line item itself (already has name/variantLabel/unitPrice/
  // quantity) rather than re-deriving those from `products`, same reasoning
  // addToCart's own trackAddToCart call already follows.
  const trackRemoval = useCallback((item) => {
    if (!item) return;
    trackRemovedFromCart(sessionId, {
      productId: item.productId,
      productName: item.variantLabel ? `${item.name} (${item.variantLabel})` : item.name,
      productPrice: item.unitPrice,
      quantity: item.quantity,
    });
  }, [sessionId]);

  // Looks the removed item(s) up from `cartItems` (the closed-over state,
  // same as `product`/`variant` params addToCart already relies on) BEFORE
  // calling setCartItems, rather than from inside the setCartItems updater
  // itself. A functional setState updater isn't guaranteed to run
  // synchronously — React can defer actually calling it until the next
  // render — so a `let removed; setCartItems(prev => { removed = ...})`
  // pattern reads `removed` before it's ever been assigned more often than
  // not, silently swallowing the tracking call. This version has no such
  // timing dependency: `cartItems` is already correct at call time.
  const updateQuantityByProduct = useCallback((product, variant, newQty) => {
    const cartKey = cartKeyFor(product.id, variant?.id);
    if (newQty <= 0) trackRemoval(cartItems.find((i) => i.cartKey === cartKey) || null);
    setCartItems((prev) => (newQty <= 0 ? prev.filter((i) => i.cartKey !== cartKey) : prev.map((i) => (i.cartKey === cartKey ? { ...i, quantity: newQty } : i))));
  }, [cartItems, trackRemoval]);

  const updateCartItemQty = useCallback((cartKey, newQty) => {
    if (newQty <= 0) trackRemoval(cartItems.find((i) => i.cartKey === cartKey) || null);
    setCartItems((prev) => (newQty <= 0 ? prev.filter((i) => i.cartKey !== cartKey) : prev.map((i) => (i.cartKey === cartKey ? { ...i, quantity: newQty } : i))));
  }, [cartItems, trackRemoval]);

  const updateGroupQuantity = useCallback((productId, newQty) => {
    if (newQty <= 0) cartItems.filter((i) => i.productId === productId).forEach(trackRemoval);
    setCartItems((prev) =>
      newQty <= 0 ? prev.filter((i) => i.productId !== productId) : prev.map((i) => (i.productId === productId ? { ...i, quantity: newQty } : i))
    );
  }, [cartItems, trackRemoval]);

  const removeCartItemsForProduct = useCallback((productId) => {
    cartItems.filter((i) => i.productId === productId).forEach(trackRemoval);
    setCartItems((prev) => prev.filter((i) => i.productId !== productId));
  }, [cartItems, trackRemoval]);

  const removeInvalidCartItems = useCallback((invalidItems) => {
    const keysToRemove = new Set((invalidItems || []).map((i) => cartKeyFor(i.productId, i.variantId)));
    setCartItems((prev) => prev.filter((i) => !keysToRemove.has(i.cartKey)));
  }, []);

  const cartItemQuantities = useMemo(() => {
    const map = {};
    cartItems.forEach((i) => {
      map[i.cartKey] = i.quantity;
    });
    return map;
  }, [cartItems]);

  const groupQuantities = useMemo(() => {
    const map = {};
    cartItems.forEach((i) => {
      const product = products.find((p) => p.id === i.productId);
      if (product?.independentVariants && !(i.productId in map)) {
        map[i.productId] = i.quantity;
      }
    });
    return map;
  }, [cartItems, products]);

  const totalCartCount = useMemo(() => {
    const countedGroupProductIds = new Set();
    let count = 0;
    cartItems.forEach((i) => {
      const product = products.find((p) => p.id === i.productId);
      if (product?.independentVariants) {
        if (countedGroupProductIds.has(i.productId)) return;
        countedGroupProductIds.add(i.productId);
      }
      count += i.quantity;
    });
    return count;
  }, [cartItems, products]);

  const cartItemImages = useMemo(() => {
    const seenProductIds = new Set();
    const images = [];
    for (const i of cartItems) {
      if (seenProductIds.has(i.productId)) continue;
      seenProductIds.add(i.productId);
      if (i.image) images.push(i.image);
      if (images.length >= 2) break;
    }
    return images;
  }, [cartItems]);

  return {
    cartItems,
    setCartItems,
    toastMessage,
    addToCart,
    updateQuantityByProduct,
    updateCartItemQty,
    updateGroupQuantity,
    removeCartItemsForProduct,
    removeInvalidCartItems,
    cartItemQuantities,
    groupQuantities,
    totalCartCount,
    cartItemImages,
  };
}
